import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Bot, AlertCircle, Menu, Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useSalesCoach } from '../../hooks/useSalesCoach';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { UseCaseCards } from './UseCaseCards';
import { ChatSidebar } from './ChatSidebar';
import { ThinkingIndicator } from './ThinkingIndicator';
import { MeetingSelector } from './MeetingSelector';
import { DealSelector } from './DealSelector';
import { LeadSearchSelector } from './LeadSearchSelector';
import { TranscriptInput } from './TranscriptInput';
import { FollowUpModeSelector } from './FollowUpModeSelector';
import { ScriptTypeSelector, ScriptType } from './ScriptTypeSelector';
import { ScriptLeadSelector } from './ScriptLeadSelector';

const SCRIPT_GUIDE_MAP: Record<ScriptType, string> = {
  cold_call: 'sc_script_cold_call',
  followup_call: 'sc_script_followup_call',
  email: 'sc_script_email',
  linkedin: 'sc_script_linkedin',
  whatsapp: 'sc_script_whatsapp',
  meeting_agenda: 'sc_meeting_prep',
};
import { useProfile } from '../../contexts/ProfileContext';
import { useTimezone } from '../../hooks/useTimezone';
import { parseMarkdown } from '../../utils/markdownRenderer';
import {
  buildMeetingPrepContext,
  buildMeetingFeedbackContext,
  buildDealAdviceContext,
  buildLeadIntelContext,
  buildFollowUpStrategyContext,
  buildSalesScriptsContext,
  buildBaselineCompanyContext,
} from '../../services/salesCoachContextService';

type SelectorMode =
  | null
  | 'meeting-prep'
  | 'meeting-feedback'
  | 'deal-advice'
  | 'lead-intel'
  | 'follow-up-strategy'
  | 'follow-up-lead-search'
  | 'sales-scripts'
  | 'sales-scripts-lead-search';

const SalesCoachPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeChatThreadId, setActiveChatThreadId] = useState<string | null>(() => {
    return localStorage.getItem('activeChatThreadId') || null;
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [selectorMode, setSelectorMode] = useState<SelectorMode>(null);
  const [transcriptMeeting, setTranscriptMeeting] = useState<any>(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [pendingScriptType, setPendingScriptType] = useState<ScriptType | null>(null);
  const { profile } = useProfile();
  const tz = useTimezone();
  const firstName = profile?.full_name?.split(/\s+/)[0] || '';
  const deepLinkProcessed = useRef(false);
  const sidebarRefreshRef = useRef<(() => void) | null>(null);

  const {
    messages,
    isLoading,
    isSending,
    isThinking,
    error,
    streamingMessage,
    sendMessage,
  } = useSalesCoach({ chatThreadId: activeChatThreadId });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (activeChatThreadId) {
      localStorage.setItem('activeChatThreadId', activeChatThreadId);
    }
  }, [activeChatThreadId]);

  const scrollRafRef = useRef<number | null>(null);
  const userScrolledUpRef = useRef(false);
  const programmaticScrollUntilRef = useRef(0);
  const prevMessagesLenRef = useRef(0);
  const sessionSwitchRef = useRef(false);

  const handleContainerScroll = useCallback(() => {
    if (Date.now() < programmaticScrollUntilRef.current) return;
    const container = messagesContainerRef.current;
    if (!container) return;

    const distFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distFromBottom > 250) {
      userScrolledUpRef.current = true;
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    } else {
      userScrolledUpRef.current = false;
    }
  }, []);

  const scrollToBottom = useCallback((instant?: boolean) => {
    const container = messagesContainerRef.current;
    if (!container) return;

    if (scrollRafRef.current) {
      cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }

    programmaticScrollUntilRef.current = Date.now() + 1500;

    if (instant) {
      container.scrollTop = container.scrollHeight - container.clientHeight;
      return;
    }

    const step = () => {
      const target = container.scrollHeight - container.clientHeight;
      const diff = target - container.scrollTop;
      if (diff <= 1) {
        container.scrollTop = target;
        scrollRafRef.current = null;
        return;
      }
      container.scrollTop += diff * 0.15;
      scrollRafRef.current = requestAnimationFrame(step);
    };
    scrollRafRef.current = requestAnimationFrame(step);
  }, []);

  useEffect(() => {
    const prevLen = prevMessagesLenRef.current;
    const curLen = messages.length;
    prevMessagesLenRef.current = curLen;

    if (curLen === 0) return;

    if (sessionSwitchRef.current) {
      sessionSwitchRef.current = false;
      requestAnimationFrame(() => scrollToBottom(true));
      return;
    }

    if (curLen > prevLen && !userScrolledUpRef.current) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (!streamingMessage && !isThinking) return;
    if (!userScrolledUpRef.current) {
      scrollToBottom();
    }
  }, [streamingMessage, isThinking, scrollToBottom]);

  const handleNewChat = useCallback(() => {
    const newThreadId = crypto.randomUUID();
    setActiveChatThreadId(newThreadId);
    userScrolledUpRef.current = false;
    prevMessagesLenRef.current = 0;
    sessionSwitchRef.current = true;
    return newThreadId;
  }, []);

  const handleSessionSelect = (chatThreadId: string) => {
    setActiveChatThreadId(chatThreadId);
    userScrolledUpRef.current = false;
    prevMessagesLenRef.current = 0;
    sessionSwitchRef.current = true;
  };

  useEffect(() => {
    if (!activeChatThreadId) {
      handleNewChat();
    }
  }, []);

  const refreshSidebar = useCallback(() => {
    sidebarRefreshRef.current?.();
  }, []);

  const startContextSession = useCallback(async (
    contextBuilder: () => Promise<string>,
    userMessage: string,
    sessionTitle: string,
    contextType: string,
    contextParams: Record<string, string>,
    guideType?: string
  ) => {
    setContextLoading(true);
    try {
      const newThreadId = handleNewChat();
      const context = await contextBuilder();

      sendMessage(userMessage, context, sessionTitle, {
        contextType,
        contextParams,
        targetChatThreadId: newThreadId,
        guideType,
      });
      setContextLoading(false);

      setTimeout(refreshSidebar, 2000);
    } catch (err) {
      console.error('Failed to build context:', err);
      setContextLoading(false);
    }
  }, [handleNewChat, sendMessage, refreshSidebar]);

  const handleMeetingPrepSelect = useCallback(async (meeting: any) => {
    setSelectorMode(null);
    const title = `Meeting Prep: ${meeting.company_name}`;
    const msg = `I have an upcoming meeting with ${meeting.dm_full_name} from ${meeting.company_name} on ${meeting.meeting_date}. Help me prepare for this meeting.`;

    await startContextSession(
      () => buildMeetingPrepContext(meeting.lead_id, meeting.meeting_id, tz),
      msg,
      title,
      'meeting-prep',
      { leadId: meeting.lead_id, meetingId: meeting.meeting_id },
      'sc_meeting_prep'
    );
  }, [startContextSession]);

  const handleMeetingFeedbackSelect = useCallback(async (meeting: any) => {
    setSelectorMode(null);
    if (meeting.transcript && !meeting.transcript.startsWith('http')) {
      const title = `Meeting Review: ${meeting.company_name}`;
      const msg = `I just had a meeting with ${meeting.dm_full_name} from ${meeting.company_name} on ${meeting.meeting_date}. Please review my meeting and give me feedback.`;

      await startContextSession(
        () => buildMeetingFeedbackContext(meeting.lead_id, meeting.meeting_id, meeting.transcript, tz),
        msg,
        title,
        'meeting-feedback',
        { leadId: meeting.lead_id, meetingId: meeting.meeting_id, transcript: meeting.transcript },
        'sc_meeting_feedback'
      );
    } else {
      setTranscriptMeeting(meeting);
    }
  }, [startContextSession]);

  const handleTranscriptSubmit = useCallback(async (transcript: string) => {
    if (!transcriptMeeting) return;
    const meeting = transcriptMeeting;
    setTranscriptMeeting(null);
    const title = `Meeting Review: ${meeting.company_name}`;
    const msg = `I just had a meeting with ${meeting.dm_full_name} from ${meeting.company_name} on ${meeting.meeting_date}. Here is the meeting transcript:\n\n${transcript}\n\nPlease review my meeting and give me detailed feedback.`;

    await startContextSession(
      () => buildMeetingFeedbackContext(meeting.lead_id, meeting.meeting_id, transcript, tz),
      msg,
      title,
      'meeting-feedback',
      { leadId: meeting.lead_id, meetingId: meeting.meeting_id, transcript },
      'sc_meeting_feedback'
    );
  }, [transcriptMeeting, startContextSession]);

  const handleTranscriptSkip = useCallback(async () => {
    if (!transcriptMeeting) return;
    const meeting = transcriptMeeting;
    setTranscriptMeeting(null);
    const title = `Meeting Review: ${meeting.company_name}`;
    const msg = `I just had a meeting with ${meeting.dm_full_name} from ${meeting.company_name} on ${meeting.meeting_date}. I don't have the transcript, but please give me feedback based on what you know about this prospect and our interactions.`;

    await startContextSession(
      () => buildMeetingFeedbackContext(meeting.lead_id, meeting.meeting_id, undefined, tz),
      msg,
      title,
      'meeting-feedback',
      { leadId: meeting.lead_id, meetingId: meeting.meeting_id },
      'sc_meeting_feedback'
    );
  }, [transcriptMeeting, startContextSession]);

  const handleDealSelect = useCallback(async (deal: any) => {
    setSelectorMode(null);
    const value = deal.deal_value ? `$${Number(deal.deal_value).toLocaleString()}` : 'not set';
    const stage = deal.pipeline_stage?.replace(/_/g, ' ') || 'unknown';
    const title = `Deal Strategy: ${deal.company_name}`;
    const msg = `I need advice on my deal with ${deal.company_name}. It's currently in the "${stage}" stage with a deal value of ${value}. Help me figure out the best strategy to advance this deal.`;

    await startContextSession(
      () => buildDealAdviceContext(deal.lead_id, deal.deal_id, tz),
      msg,
      title,
      'deal-advice',
      { leadId: deal.lead_id, dealId: deal.deal_id },
      'sc_deal_advice'
    );
  }, [startContextSession]);

  const handleLeadIntelSelect = useCallback(async (lead: any) => {
    setSelectorMode(null);
    const title = `Lead Intel: ${lead.company_name}`;
    const msg = `Tell me everything you know about ${lead.dm_full_name || 'the decision maker'} at ${lead.company_name}. Give me a full intelligence briefing on this lead.`;

    await startContextSession(
      () => buildLeadIntelContext(lead.lead_id, tz),
      msg,
      title,
      'lead-intel',
      { leadId: lead.lead_id },
      'sc_lead_info'
    );
  }, [startContextSession]);

  const handleFollowUpLeadSelect = useCallback(async (lead: any) => {
    setSelectorMode(null);
    const title = `Follow Up: ${lead.company_name}`;
    const msg = `Help me create a follow-up strategy for ${lead.dm_full_name || 'the decision maker'} at ${lead.company_name}. What's the best way to follow up based on our engagement history?`;

    await startContextSession(
      () => buildFollowUpStrategyContext(lead.lead_id, tz),
      msg,
      title,
      'follow-up-strategy',
      { leadId: lead.lead_id },
      'sc_follow_up_strategy'
    );
  }, [startContextSession]);

  const handleFollowUpGeneral = useCallback(async () => {
    setSelectorMode(null);
    setContextLoading(true);
    try {
      const newThreadId = handleNewChat();
      const context = await buildBaselineCompanyContext();

      sendMessage(
        'I need coaching on follow-up strategy. Help me understand the best approaches for following up with leads.',
        context,
        'Follow Up Strategy',
        {
          contextType: 'follow-up-strategy',
          contextParams: {},
          targetChatThreadId: newThreadId,
          guideType: 'sc_follow_up_strategy',
        }
      );
      setContextLoading(false);
      setTimeout(refreshSidebar, 2000);
    } catch (err) {
      console.error('Failed to start general follow-up session:', err);
      setContextLoading(false);
    }
  }, [handleNewChat, sendMessage, refreshSidebar]);

  const handleScriptTypeSelect = useCallback((scriptType: ScriptType) => {
    setPendingScriptType(scriptType);
    setSelectorMode('sales-scripts-lead-search');
  }, []);

  const handleScriptLeadSelect = useCallback(async (lead: any) => {
    setSelectorMode(null);
    const st = pendingScriptType || 'cold_call';
    const scriptLabels: Record<string, string> = {
      cold_call: 'Cold Call Script',
      followup_call: 'Follow-Up Call Script',
      email: 'Email Template',
      linkedin: 'LinkedIn Message',
      whatsapp: 'WhatsApp Message',
      meeting_agenda: 'Meeting Script',
    };
    const label = scriptLabels[st] || st;
    const title = `${label}: ${lead.company_name}`;
    const msg = `Write me a ${label.toLowerCase()} for ${lead.dm_full_name || 'the decision maker'} at ${lead.company_name}. Make it personalized based on what we know about this lead.`;

    await startContextSession(
      () => buildSalesScriptsContext(st, lead.lead_id, tz),
      msg,
      title,
      'sales-scripts',
      { scriptType: st, leadId: lead.lead_id },
      SCRIPT_GUIDE_MAP[st as ScriptType] || 'sc_script_cold_call'
    );
    setPendingScriptType(null);
  }, [pendingScriptType, startContextSession]);

  const handleScriptGeneric = useCallback(async () => {
    setSelectorMode(null);
    const st = pendingScriptType || 'cold_call';
    const scriptLabels: Record<string, string> = {
      cold_call: 'Cold Call Script',
      followup_call: 'Follow-Up Call Script',
      email: 'Email Template',
      linkedin: 'LinkedIn Message',
      whatsapp: 'WhatsApp Message',
      meeting_agenda: 'Meeting Script',
    };
    const label = scriptLabels[st] || st;

    setContextLoading(true);
    try {
      const newThreadId = handleNewChat();
      const context = await buildSalesScriptsContext(st, undefined, tz);

      sendMessage(
        `Write me a ${label.toLowerCase()}. Use my company's pitch book and sales playbook to make it compelling.`,
        context,
        label,
        {
          contextType: 'sales-scripts',
          contextParams: { scriptType: st },
          targetChatThreadId: newThreadId,
          guideType: SCRIPT_GUIDE_MAP[st as ScriptType] || 'sc_script_cold_call',
        }
      );
      setContextLoading(false);
      setTimeout(refreshSidebar, 2000);
    } catch (err) {
      console.error('Failed to start script session:', err);
      setContextLoading(false);
    }
    setPendingScriptType(null);
  }, [pendingScriptType, handleNewChat, sendMessage, refreshSidebar]);

  useEffect(() => {
    if (deepLinkProcessed.current) return;
    const action = searchParams.get('action');
    const leadId = searchParams.get('leadId');
    const meetingId = searchParams.get('meetingId');
    const dealId = searchParams.get('dealId');
    const scriptType = searchParams.get('scriptType');

    if (!action) return;
    deepLinkProcessed.current = true;
    setSearchParams({}, { replace: true });

    if (action === 'meeting-prep' && leadId && meetingId) {
      setContextLoading(true);
      const newTid = handleNewChat();
      buildMeetingPrepContext(leadId, meetingId, tz).then((context) => {
        sendMessage('Help me prepare for this upcoming meeting.', context, 'Meeting Prep', {
          contextType: 'meeting-prep',
          contextParams: { leadId, meetingId },
          targetChatThreadId: newTid,
          guideType: 'sc_meeting_prep',
        });
        setContextLoading(false);
        setTimeout(refreshSidebar, 2000);
      }).catch(() => setContextLoading(false));
    } else if (action === 'meeting-feedback' && leadId && meetingId) {
      handleNewChat();
      setTranscriptMeeting({ lead_id: leadId, meeting_id: meetingId, company_name: 'Meeting', dm_full_name: 'Contact', meeting_date: 'Recent' });
    } else if (action === 'deal-advice' && leadId && dealId) {
      setContextLoading(true);
      const newTid = handleNewChat();
      buildDealAdviceContext(leadId, dealId, tz).then((context) => {
        sendMessage('Help me with a strategy for this deal.', context, 'Deal Strategy', {
          contextType: 'deal-advice',
          contextParams: { leadId, dealId },
          targetChatThreadId: newTid,
          guideType: 'sc_deal_advice',
        });
        setContextLoading(false);
        setTimeout(refreshSidebar, 2000);
      }).catch(() => setContextLoading(false));
    } else if (action === 'lead-intel' && leadId) {
      setContextLoading(true);
      const newTid = handleNewChat();
      buildLeadIntelContext(leadId, tz).then((context) => {
        sendMessage('Give me a full intelligence briefing on this lead.', context, 'Lead Intel', {
          contextType: 'lead-intel',
          contextParams: { leadId },
          targetChatThreadId: newTid,
          guideType: 'sc_lead_info',
        });
        setContextLoading(false);
        setTimeout(refreshSidebar, 2000);
      }).catch(() => setContextLoading(false));
    } else if (action === 'follow-up-strategy' && leadId) {
      setContextLoading(true);
      const newTid = handleNewChat();
      buildFollowUpStrategyContext(leadId, tz).then((context) => {
        sendMessage('Help me create a follow-up strategy for this lead.', context, 'Follow Up Strategy', {
          contextType: 'follow-up-strategy',
          contextParams: { leadId },
          targetChatThreadId: newTid,
          guideType: 'sc_follow_up_strategy',
        });
        setContextLoading(false);
        setTimeout(refreshSidebar, 2000);
      }).catch(() => setContextLoading(false));
    } else if (action === 'sales-scripts' && scriptType) {
      setContextLoading(true);
      const newTid = handleNewChat();
      const lid = leadId || undefined;
      buildSalesScriptsContext(scriptType, lid, tz).then((context) => {
        sendMessage(`Write me a ${scriptType.replace(/_/g, ' ')} script.`, context, 'Sales Script', {
          contextType: 'sales-scripts',
          contextParams: { scriptType, ...(leadId ? { leadId } : {}) },
          targetChatThreadId: newTid,
          guideType: SCRIPT_GUIDE_MAP[scriptType as ScriptType] || 'sc_script_cold_call',
        });
        setContextLoading(false);
        setTimeout(refreshSidebar, 2000);
      }).catch(() => setContextLoading(false));
    }
  }, [searchParams]);

  const handleSend = useCallback((message: string, ...rest: any[]) => {
    userScrolledUpRef.current = false;
    sendMessage(message, ...rest);
    setTimeout(refreshSidebar, 2000);
  }, [sendMessage, refreshSidebar]);

  const hasMessages = messages.length > 0 || streamingMessage || isThinking;
  const isProcessing = isSending || contextLoading;

  return (
    <div className="flex h-full overflow-hidden bg-gradient-to-br from-white to-[#1875FF]/5">
      <ChatSidebar
        activeChatThreadId={activeChatThreadId}
        onSessionSelect={handleSessionSelect}
        onNewChat={handleNewChat}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        refreshRef={sidebarRefreshRef}
      />

      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <div className="lg:hidden bg-gradient-to-br from-white to-[#1875FF]/5 border-b-2 border-[#1875FF]/20 px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="text-gray-600 hover:text-gray-900 transition-colors"
          >
            <Menu size={24} />
          </button>
          <h1 className="text-lg font-extrabold text-gray-900 font-display">Sales Coach</h1>
        </div>

        <div className="flex-1 overflow-y-auto" ref={messagesContainerRef} onScroll={handleContainerScroll}>
          <div className="max-w-5xl mx-auto px-4 pt-6 pb-4 sm:px-6">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-start gap-3 shadow-sm">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-red-900 text-sm font-bold">Error</p>
                  <p className="text-red-700 text-sm mt-1 font-medium">{error}</p>
                </div>
              </div>
            )}

            {!hasMessages && !isLoading && !contextLoading && (
              <div className="py-8">
                <div className="text-center mb-10">
                  <div className="relative inline-block mb-6">
                    <div className="absolute inset-0 bg-[#05E0D1]/30 opacity-60 rounded-full blur-2xl animate-pulse"></div>
                    <div className="absolute inset-0 bg-gradient-to-br from-[#1875FF]/20 to-[#05E0D1]/20 rounded-full blur-xl animate-pulse" style={{ animationDelay: '500ms' }}></div>
                    <div className="relative w-20 h-20 bg-gradient-to-br from-[#1875FF] via-[#05E0D1] to-[#52B5AE] rounded-full flex items-center justify-center shadow-2xl ring-4 ring-[#05E0D1]/40 transition-all duration-300 overflow-hidden">
                      {!imageError ? (
                        <img
                          src="https://utimppxbdeptpoyotmcx.supabase.co/storage/v1/object/public/agents-avatars/550e8400-e29b-41d4-a716-446655440001/sales-coach.png"
                          alt="Sales Coach"
                          className="w-full h-full object-cover"
                          onError={() => setImageError(true)}
                          loading="eager"
                        />
                      ) : (
                        <Bot className="w-10 h-10 text-white" />
                      )}
                    </div>
                  </div>
                  <h2 className="text-3xl font-extrabold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-[#1875FF] to-[#05E0D1] font-display tracking-tight">
                    Hey{firstName ? ` ${firstName}` : ''}, I am your AI Sales Coach!
                  </h2>
                  <p className="text-gray-500 max-w-xl mx-auto font-medium">
                    I can help you prepare for meetings, review your performance, advise on deals, and give you intelligence on any lead.
                  </p>
                </div>

                <UseCaseCards
                  onMeetingPrep={() => setSelectorMode('meeting-prep')}
                  onMeetingFeedback={() => setSelectorMode('meeting-feedback')}
                  onDealAdvice={() => setSelectorMode('deal-advice')}
                  onLeadIntel={() => setSelectorMode('lead-intel')}
                  onFollowUpStrategy={() => setSelectorMode('follow-up-strategy')}
                  onSalesScripts={() => setSelectorMode('sales-scripts')}
                  disabled={isProcessing}
                />
              </div>
            )}

            {(isLoading || contextLoading) && !hasMessages && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Loader2 className="w-8 h-8 text-[#1875FF] animate-spin" />
                {contextLoading && (
                  <p className="text-sm text-gray-500 font-medium animate-pulse">
                    Loading CRM data and building context...
                  </p>
                )}
              </div>
            )}

            {hasMessages && (
              <div className="space-y-4">
                {messages.map((message, idx) => {
                  if (
                    streamingMessage &&
                    idx === messages.length - 1 &&
                    message.role === 'assistant'
                  ) {
                    return null;
                  }
                  return (
                    <ChatMessage
                      key={message.message_id}
                      role={message.role as 'user' | 'assistant'}
                      content={message.content}
                      timestamp={message.created_at}
                      userAvatarUrl={profile?.profile_picture_url}
                    />
                  );
                })}

                {isThinking && !streamingMessage && (
                  <ThinkingIndicator
                    avatarUrl="https://utimppxbdeptpoyotmcx.supabase.co/storage/v1/object/public/agents-avatars/550e8400-e29b-41d4-a716-446655440001/sales-coach.png"
                    avatarFallback={imageError}
                    onAvatarError={() => setImageError(true)}
                  />
                )}

                {streamingMessage && (
                  <div className="flex gap-3 mb-6">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-br from-[#1875FF] to-[#05E0D1] shadow-md overflow-hidden">
                      {!imageError ? (
                        <img
                          src="https://utimppxbdeptpoyotmcx.supabase.co/storage/v1/object/public/agents-avatars/550e8400-e29b-41d4-a716-446655440001/sales-coach.png"
                          alt="Sales Coach"
                          className="w-full h-full object-cover"
                          onError={() => setImageError(true)}
                          loading="eager"
                        />
                      ) : (
                        <Bot className="w-5 h-5 text-white" />
                      )}
                    </div>
                    <div className="flex-1 max-w-3xl">
                      <div className="rounded-2xl px-5 py-4 bg-gradient-to-br from-white to-[#1875FF]/5 text-gray-900 border-2 border-[#1875FF]/20 shadow-lg">
                        <div className="text-gray-900 leading-relaxed">
                          {parseMarkdown(streamingMessage)}
                          <span className="inline-block w-1.5 h-4 bg-[#1875FF]/70 ml-0.5 animate-pulse rounded-sm align-baseline"></span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>

        <div className="border-t-2 border-[#1875FF]/20 bg-gradient-to-br from-white to-[#1875FF]/5 shadow-xl">
          <div className="max-w-5xl mx-auto px-4 py-5 sm:px-6">
            <ChatInput
              onSend={handleSend}
              disabled={isProcessing}
              placeholder={contextLoading ? 'Loading context...' : isThinking ? 'Thinking...' : isSending ? 'Generating response...' : 'Ask me anything about sales...'}
            />
          </div>
        </div>
      </div>

      {selectorMode === 'meeting-prep' && (
        <MeetingSelector
          mode="upcoming"
          onSelect={handleMeetingPrepSelect}
          onClose={() => setSelectorMode(null)}
        />
      )}

      {selectorMode === 'meeting-feedback' && (
        <MeetingSelector
          mode="attended"
          onSelect={handleMeetingFeedbackSelect}
          onClose={() => setSelectorMode(null)}
        />
      )}

      {selectorMode === 'deal-advice' && (
        <DealSelector
          onSelect={handleDealSelect}
          onClose={() => setSelectorMode(null)}
        />
      )}

      {selectorMode === 'lead-intel' && (
        <LeadSearchSelector
          onSelect={handleLeadIntelSelect}
          onClose={() => setSelectorMode(null)}
        />
      )}

      {selectorMode === 'follow-up-strategy' && (
        <FollowUpModeSelector
          onPickLead={() => setSelectorMode('follow-up-lead-search')}
          onGeneralAdvice={handleFollowUpGeneral}
          onClose={() => setSelectorMode(null)}
        />
      )}

      {selectorMode === 'follow-up-lead-search' && (
        <LeadSearchSelector
          onSelect={handleFollowUpLeadSelect}
          onClose={() => setSelectorMode(null)}
        />
      )}

      {selectorMode === 'sales-scripts' && (
        <ScriptTypeSelector
          onSelect={handleScriptTypeSelect}
          onClose={() => setSelectorMode(null)}
        />
      )}

      {selectorMode === 'sales-scripts-lead-search' && (
        <ScriptLeadSelector
          scriptLabel={(() => {
            const labels: Record<string, string> = { cold_call: 'Cold Call Script', followup_call: 'Follow-Up Call Script', email: 'Email Template', linkedin: 'LinkedIn Message', whatsapp: 'WhatsApp Message', meeting_agenda: 'Meeting Script' };
            return labels[pendingScriptType || ''] || 'Script';
          })()}
          onSelect={handleScriptLeadSelect}
          onSkip={handleScriptGeneric}
          onClose={() => {
            setSelectorMode(null);
            setPendingScriptType(null);
          }}
        />
      )}

      {transcriptMeeting && (
        <TranscriptInput
          meetingInfo={{
            company_name: transcriptMeeting.company_name,
            dm_full_name: transcriptMeeting.dm_full_name,
            meeting_date: transcriptMeeting.meeting_date,
          }}
          onSubmit={handleTranscriptSubmit}
          onSkip={handleTranscriptSkip}
          onClose={() => setTranscriptMeeting(null)}
        />
      )}
    </div>
  );
};

export default SalesCoachPage;
